import {FC, useEffect} from "react";
import {LineChart} from "@mui/x-charts/LineChart";
import {Box, styled, Typography, useTheme, Skeleton} from "@mui/material";
import CustomChartAxisTooltip from "./CustomChartTooltip";
import dayjs from "dayjs";
import useLessonsCompletedChart from "@/hooks/useLessonsCompletedChart";
import {useTeamProgressContext} from "@/contexts/TeamProgressContext";
import {palette} from "@/constants";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import { TeamProgressSection } from "@/enums";

const LessonsCompletedChart: FC = () => {
	const theme = useTheme();
	const {t} = useSSTranslation();
	const {filters: {archivedUsers, dateFrom, dateTo}, updateSectionState} = useTeamProgressContext();
	const {lessonsCompletedChartData, apiResult, getLessonsCompletedChartData} = useLessonsCompletedChart();
	const isFetching = !apiResult.statusCode || apiResult.isLoading || !lessonsCompletedChartData;

	useEffect(() => {
		getLessonsCompletedChartData(dateFrom, dateTo, archivedUsers, {throttle: false});
	}, [dateFrom.toDateString(), dateTo.toDateString(), archivedUsers]);

	useEffect(() => {
		updateSectionState(isFetching, TeamProgressSection.chart);
	}, [isFetching]);

	return (
		<Container gap={3}>
			<Typography variant="h6" color="#F9F9F9B2">{t("team_progress_widget_completedLessons", "Completed Lessons")}</Typography>
			{apiResult.isLoading ? (
				<Skeleton variant="rectangular" width="100%" height={370} sx={{borderRadius: 2}} />
			) : (
				<LineChart
					xAxis={[{
						dataKey: "date",
						valueFormatter: value => dayjs(value[lessonsCompletedChartData?.axisDataType == "day"? 0 : 1]).format(lessonsCompletedChartData?.axisDataType == "day"? "MMM D" : "MMM"),
						tickInterval: (value) => lessonsCompletedChartData?.tickInterval.includes(value[lessonsCompletedChartData?.axisDataType == "day"? 0 : 1]),
						scaleType: "point"
					}]}
					series={[
						{
							dataKey: "lessonsCompleted",
							curve: "catmullRom",
							area: true,
							color: theme.palette.primary.main,
							showMark: false
						}
					]}
					dataset={lessonsCompletedChartData?.data ?? []}
					height={370}
					tooltip={{
						trigger: "axis"
					}}
					slots={{
						axisContent: CustomChartAxisTooltip
					}}
					sx={{
						"& .MuiAreaElement-root": {
							fill: "url(#areaGradient)"
						}
					}}
				>
					<linearGradient id="areaGradient" x1="100%" y1="0%" x2="100%" y2="100%">
						<stop offset="0" stopColor="#C27A08" />
						<stop offset="1" stopColor="#141414" />
					</linearGradient>
				</LineChart>
			)}
		</Container>
	);
}

const Container = styled(Box)(() => ({
	borderRadius: "8px",
	height: "394px",
	backgroundColor: palette.greyDark,
	border: "solid 1px #A1A1A026",
	padding: "25px 15px"
}));

export default LessonsCompletedChart;